import { Controller } from "@hotwired/stimulus"

// วงดวงดาวย้ายในแท็บดาวย้าย — วาด SVG วงเล็ก (แถบพับ) + วงใหญ่ (เต็มจอ) จากตำแหน่งดาวจรวันนี้
// วงใหญ่เลื่อนดูดาวล่วงหน้าได้ 1 ปี (แถบเลื่อน / ปัดซ้าย-ขวาบนวง / ปุ่มเล่น) และแตะดาวดูว่าอยู่เรือนไหน
// ตำแหน่งราศีคงที่: เมษ 12 นาฬิกา ไล่ทวนเข็ม (ตามวงหน้า landing และวงดวง PDF)
// ︎ ต่อท้ายสัญลักษณ์ราศี = บังคับ text presentation กันเบราว์เซอร์เรนเดอร์เป็นอีโมจิ
const SIGNS = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"].map((s) => s + "︎")
// ชื่อเรือนแบบย่อสำหรับบนวง (คำเต็มอยู่ใน TransitWheelHelper ใช้ในลิสต์ใต้วง)
const HOUSE_NAMES = ["ตัวคุณ", "การเงิน", "เดินทาง", "บ้าน", "บริวาร", "อุปสรรค",
  "คู่ครอง", "สุขภาพ", "ความสำเร็จ", "การงาน", "สิ่งที่ได้", "เสื่อมเสีย"]
const TH_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."]
const TH_DAYS = ["อาทิตย์", "จันทร์", "อังคาร", "พุธ", "พฤหัสบดี", "ศุกร์", "เสาร์"]

const GOLD = "#E8C87A", GOLD_DIM = "#B9995A", ORANGE = "#FF9E4F"
// สีวงใหญ่: แถบชื่อเรือนดำ พื้นในขาวนวล (Lek เลือกจาก 6 แบบทอง/ขาว/ดำ 29 ก.ย. 69) — วงจิ๋วบนแถบพับยังพื้นดำ
const WHEEL_THEME = { band: "#15110a", bandText: "#CDBF9C", bandTextLagna: GOLD, bandLine: "#5d4c25",
  inner: "#FAF6EC", ring: "#B98A3A", line: "#9C8A66", glyph: "#A8925E", tint: "rgba(214,160,60,0.16)",
  ink: "#2B2418", sel: "#9A7426", hot: "#D65A15" }
const MINI_THEME = { inner: null, ring: "#7a6230", line: "#5d4c25", tint: "rgba(232,200,122,0.10)",
  ink: "#EFE4C8", sel: GOLD, hot: ORANGE }
const PLAY_DAYS_PER_SEC = 14 // กดเล่น ~26 วินาทีครบปี
const SWIPE_DAYS_PER_PX = 0.35

export default class extends Controller {
  static targets = ["mini", "big", "overlay", "caption", "card", "play", "date", "dateSub",
    "todayBtn", "slider", "ticks", "legend", "hotCta"]
  static values = { wheel: Object, hotCardId: String, timelineUrl: String }

  // ถ้อยคำ/วันที่ตามภาษาของหน้า มากับ wheel value (ranges_page.wheel.js) — ค่าตั้งต้นไทยเผื่อหน้าเก่าในแคช
  get days() { return this.wheelValue.days || TH_DAYS }
  get months() { return this.wheelValue.months || TH_MONTHS }
  get yearOffset() { return this.wheelValue.yearOffset ?? 543 }

  label(key, vars = {}, fallback = "") {
    const text = (this.wheelValue.labels || {})[key] ?? fallback
    return Object.entries(vars).reduce((out, [k, v]) => out.replaceAll(`%{${k}}`, v), text)
  }

  // สัญลักษณ์ดาวที่วาด — ไทยเลขไทย · อังกฤษสัญลักษณ์สากล (sym ยังเป็นคีย์ภายในเหมือนเดิม)
  glyph(sym) { return (this.wheelValue.glyphs || {})[sym] || sym }

  connect() {
    this.day = 0
    this.selected = null
    this.timeline = null
    if (this.hasMiniTarget) this.draw(this.miniTarget, true)
    if (this.hasBigTarget) {
      this.draw(this.bigTarget, false)
      this.bindWheelGestures()
    }
    // เปิดหน้าด้วย #wheel = กางวงเต็มจอทันที (ใช้ทำ deep link จาก LINE/broadcast ได้)
    if (window.location.hash === "#wheel") this.expand()
  }

  disconnect() {
    document.body.style.overflow = ""
    this.stopPlay()
  }

  expand() {
    this.overlayTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this.loadTimeline()
  }

  close() {
    this.stopPlay()
    this.overlayTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ปิดวงแล้วเลื่อนไปการ์ดดาวที่เพิ่งย้าย (การ์ดเปิดอ่านเองด้วยปุ่ม "อ่าน" ของมันตามเดิม)
  readHot() {
    this.scrollToCard(this.hotCardIdValue)
  }

  scrollToCard(id) {
    this.close()
    const card = document.getElementById(id)
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // ---- ข้อมูลดาวล่วงหน้า (โหลดครั้งแรกที่กางวง) ----
  async loadTimeline() {
    if (this.timeline || this.loading || !this.hasTimelineUrlValue) return
    this.loading = true
    try {
      const res = await fetch(this.timelineUrlValue, { headers: { Accept: "application/json" } })
      if (!res.ok) throw new Error(res.status)
      this.timeline = await res.json()
      this.start = new Date()
      this.sliderTarget.max = this.timeline.days.length - 1
      this.sliderTarget.disabled = false
      this.playTarget.disabled = false
      this.renderTicks()
    } catch (e) {
      // โหลดไม่ได้ก็ยังดูวงวันนี้ได้ตามเดิม แค่ซ่อนแถบเลื่อน
      this.sliderTarget.closest("div.rounded-xl").classList.add("hidden")
    } finally {
      this.loading = false
    }
  }

  // ตำแหน่งดาวของวันที่เลือก: [{ sym, rasi, lon }]
  planetsOn(day) {
    if (day === 0 || !this.timeline) return this.wheelValue.planets
    const lons = this.timeline.days[day]
    return this.timeline.symbols.map((sym, i) => ({ sym, lon: lons[i], rasi: Math.floor(lons[i] / 30) % 12 }))
  }

  // ---- เลื่อนวัน ----
  scrub() {
    this.stopPlay()
    this.setDay(Number(this.sliderTarget.value))
  }

  today() {
    this.stopPlay()
    this.setDay(0)
  }

  setDay(day) {
    const max = this.timeline ? this.timeline.days.length - 1 : 0
    day = Math.max(0, Math.min(max, Math.round(day)))
    if (day === this.day) return
    this.day = day
    this.sliderTarget.value = day
    this.draw(this.bigTarget, false)
    this.renderDate()
    this.renderLegend()
    if (this.selected) this.renderCard()
  }

  togglePlay() {
    if (this.playing) return this.stopPlay()
    if (!this.timeline) return
    if (this.day >= this.timeline.days.length - 1) this.setDay(0)
    this.playing = true
    this.playTarget.innerHTML = '<i class="fa-solid fa-pause text-xs"></i>'
    let last = performance.now(), pos = this.day
    const step = (now) => {
      if (!this.playing) return
      pos += ((now - last) / 1000) * PLAY_DAYS_PER_SEC
      last = now
      this.setDay(pos)
      if (this.day >= this.timeline.days.length - 1) return this.stopPlay()
      this.raf = requestAnimationFrame(step)
    }
    this.raf = requestAnimationFrame(step)
  }

  stopPlay() {
    this.playing = false
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this.hasPlayTarget) this.playTarget.innerHTML = '<i class="fa-solid fa-play text-xs"></i>'
  }

  // ปัดซ้าย-ขวาบนวง = เลื่อนวัน / แตะเฉย ๆ = เลือกดาว (วงไม่หมุน ขยับแค่ดาว)
  bindWheelGestures() {
    const svg = this.bigTarget
    let down = null, lastX = 0, pos = 0, swiping = false
    svg.addEventListener("pointerdown", (e) => {
      down = [e.clientX, e.clientY]; lastX = e.clientX; pos = this.day; swiping = false
    })
    svg.addEventListener("pointermove", (e) => {
      if (!down || !this.timeline) return
      if (!swiping && Math.abs(e.clientX - down[0]) < 8) return
      if (!swiping) { swiping = true; this.stopPlay(); svg.setPointerCapture(e.pointerId) }
      pos += (e.clientX - lastX) * SWIPE_DAYS_PER_PX
      lastX = e.clientX
      this.setDay(pos)
    })
    svg.addEventListener("pointercancel", () => { down = null })
    svg.addEventListener("pointerup", (e) => {
      const tapped = down && !swiping && Math.hypot(e.clientX - down[0], e.clientY - down[1]) < 8
      down = null
      if (!tapped) return
      const hit = e.target.closest("[data-sym]")
      this.select(hit ? hit.dataset.sym : null)
    })
  }

  // ---- แตะดาว ----
  select(sym) {
    this.selected = sym === this.selected ? null : sym
    this.draw(this.bigTarget, false)
    this.renderLegend()
    this.renderCard()
  }

  selectRow(e) {
    this.select(e.currentTarget.dataset.sym)
    this.bigTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  renderCard() {
    const card = this.cardTarget
    const p = this.selected && this.planetsOn(this.day).find((q) => q.sym === this.selected)
    if (!p) { card.classList.add("hidden"); return }
    const { lagna, names, cardIds, rasiNames, houseNames, hotSym } = this.wheelValue
    const house = ((p.rasi - lagna + 12) % 12) + 1
    const inSign = p.lon - p.rasi * 30
    const deg = Math.floor(inSign), min = Math.floor((inSign - deg) * 60)
    const hot = p.sym === hotSym && this.day === 0
    // ปุ่มอ่านคำทำนายโชว์เมื่อมีการ์ดของดาวนี้ในหน้า และดาวยังอยู่ราศีเดิมของวันนี้ (คำทำนายตรงช่วง)
    const todayRasi = this.wheelValue.planets.find((q) => q.sym === p.sym)?.rasi
    const cardId = cardIds[p.sym]
    const canRead = todayRasi === p.rasi && document.getElementById(cardId)
    card.innerHTML = `
      <div class="flex items-center gap-x-3">
        <span class="shrink-0 rounded-full flex items-center justify-center"
              style="width:34px; height:34px; font-size:19px; border:1px solid ${hot ? ORANGE : GOLD_DIM}; color:${hot ? ORANGE : GOLD};">${this.glyph(p.sym)}</span>
        <div class="min-w-0">
          <div class="text-[0.9375rem] font-semibold text-gold-cream">${this.label("planet", { name: names[p.sym] })}${hot ? this.label("just_moved") : ""}</div>
          <div class="text-xs text-gray-400">${this.label("sign_pos", { sign: rasiNames[p.rasi], deg, min: String(min).padStart(2, "0") })}</div>
        </div>
        <button type="button" class="ml-auto p-1 text-gray-400 cursor-pointer" data-action="transit-wheel#deselect" aria-label="${this.label("close")}">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div class="mt-2 text-sm text-body">${this.label("in_house_html", { color: GOLD, house, name: houseNames[house - 1], lagna: rasiNames[lagna] })}</div>
      ${canRead ? `<button type="button" data-action="transit-wheel#readSelected" class="block w-full mt-3 py-2 rounded-xl text-sm font-semibold cursor-pointer"
          style="border:1px solid ${GOLD_DIM}; color:${GOLD};">${this.label("read_planet", { name: names[p.sym] })}</button>` : ""}`
    card.classList.remove("hidden")
  }

  deselect() {
    this.select(null)
  }

  readSelected() {
    this.scrollToCard(this.wheelValue.cardIds[this.selected])
  }

  // ---- ส่วนแสดงผลรอบวง ----
  renderDate() {
    const d = new Date(this.start.getTime() + this.day * 864e5)
    const label = `${this.days[d.getDay()]} ${d.getDate()} ${this.months[d.getMonth()]} ${String(d.getFullYear() + this.yearOffset).slice(2)}`
    this.dateTarget.textContent = this.day === 0 ? this.label("today", {}, "วันนี้") : label
    this.dateSubTarget.textContent = this.day === 0 ? this.label("swipe_hint") : this.label("days_ahead", { n: this.day })
    this.todayBtnTarget.style.visibility = this.day === 0 ? "hidden" : "visible"
    this.captionTarget.textContent = this.day === 0
      ? this.label("caption", { time: this.wheelValue.updatedAt })
      : this.label("position_on", { date: label })
    if (this.hasHotCtaTarget) this.hotCtaTarget.classList.toggle("hidden", this.day !== 0)
  }

  renderTicks() {
    const out = []
    for (let m = 0; m < 12; m += 3) {
      const d = new Date(this.start); d.setMonth(d.getMonth() + m)
      out.push(`<span>${this.months[d.getMonth()]} ${String(d.getFullYear() + this.yearOffset).slice(2)}</span>`)
    }
    this.ticksTarget.innerHTML = out.join("")
  }

  // ลิสต์ใต้วง 9 ดาว — ดาวที่เพิ่งย้ายขึ้นก่อนเฉพาะวันนี้ (คลาสชุดเดียวกับ ERB เดิม)
  renderLegend() {
    const { lagna, names, rasiNames, houseNames, hotSym, legendSyms } = this.wheelValue
    const byS = Object.fromEntries(this.planetsOn(this.day).map((p) => [p.sym, p]))
    const syms = [...legendSyms].sort((a, b) => (b === hotSym && this.day === 0) - (a === hotSym && this.day === 0))
    this.legendTarget.innerHTML = syms.map((sym) => {
      const p = byS[sym]
      const hot = sym === hotSym && this.day === 0
      const sel = sym === this.selected
      const house = ((p.rasi - lagna + 12) % 12) + 1
      return `<div class="flex items-center gap-x-2 py-1.5 px-1 text-sm cursor-pointer" data-sym="${sym}" data-action="click->transit-wheel#selectRow"
          style="border-bottom:1px solid #1e1e22;${hot ? " color:#FFC08A;" : ""}${sel ? " background:rgba(232,200,122,.07);" : ""}">
        <span class="shrink-0 rounded-full flex items-center justify-center font-medium"
              style="width:24px; height:24px; font-size:13px; border:1px solid ${hot ? ORANGE : GOLD_DIM}; color:${hot ? ORANGE : GOLD};${hot ? " box-shadow:0 0 6px rgba(255,158,79,.5);" : ""}">${this.glyph(sym)}</span>
        <span class="${hot ? "" : "text-body"}">${this.label("legend", { name: names[sym], hot: hot ? this.label("hot_prefix") : "", sign: rasiNames[p.rasi] })}</span>
        <span class="ml-auto shrink-0 text-xs ${hot ? "" : "text-gray-400"}">${this.label("house", { n: house, name: houseNames[house - 1] })}</span>
      </div>`
    }).join("")
  }

  // ---- วาดวง ----
  draw(svg, mini) {
    const { lagna, lagnaName, hotSym } = this.wheelValue
    const planets = mini ? this.wheelValue.planets : this.planetsOn(this.day)
    const showHot = mini || this.day === 0
    const c = 200
    const ns = "http://www.w3.org/2000/svg"
    const uid = mini ? "twm" : "twb"
    svg.replaceChildren()
    const add = (tag, attrs, text, parent = svg) => {
      const el = document.createElementNS(ns, tag)
      for (const k in attrs) el.setAttribute(k, attrs[k])
      if (text != null) el.textContent = text
      parent.appendChild(el)
      return el
    }
    const pt = (r, deg) => {
      const a = (deg * Math.PI) / 180
      return [c + r * Math.cos(a), c - r * Math.sin(a)]
    }

    const defs = add("defs", {})
    defs.innerHTML = `<radialGradient id="hub${uid}" cx="40%" cy="35%">
        <stop offset="0%" stop-color="#2a1f0e"/><stop offset="100%" stop-color="#0d0b07"/></radialGradient>
      <linearGradient id="gold${uid}" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#F0D89A"/><stop offset="50%" stop-color="#B98A3A"/>
        <stop offset="100%" stop-color="#8A6420"/></linearGradient>`

    // วงใหญ่: แถบนอกเขียนชื่อเรือนตามแนวโค้ง (แบบวงดวงไทย ตนุ/กดุมภะ… แต่ใช้คำแปล) — ราศีย่อเป็นสัญลักษณ์เล็กด้านใน
    const T = mini ? MINI_THEME : WHEEL_THEME
    const inner = mini ? 168 : 162
    if (T.inner) add("circle", { cx: c, cy: c, r: inner, fill: T.inner })
    if (!mini) add("circle", { cx: c, cy: c, r: 179, fill: "none", stroke: T.band, "stroke-width": 34 })
    add("circle", { cx: c, cy: c, r: 196, fill: "none", stroke: `url(#gold${uid})`, "stroke-width": mini ? 10 : 5 })
    add("circle", { cx: c, cy: c, r: inner, fill: "none", stroke: T.ring, "stroke-width": 1.2 })

    for (let i = 0; i < 12; i++) {
      const b = 90 + 30 * i + 15
      const [x1, y1] = pt(mini ? 168 : 194, b)
      const [x2, y2] = pt(inner, b)
      const [x3, y3] = pt(44, b)
      if (!mini) add("line", { x1, y1, x2, y2, stroke: T.bandLine, "stroke-width": 1 })
      add("line", { x1: x2, y1: y2, x2: x3, y2: y3, stroke: T.line, "stroke-width": 1 })
      if (!mini) {
        const [dx, dy] = pt(196, b)
        add("circle", { cx: dx, cy: dy, r: 3.4, fill: GOLD })
      }
    }

    // ช่องลัคนา (เรือน 1) ย้อมสีจาง ๆ — วงใหญ่ย้อมถึงแถบชื่อเรือนด้วย
    const s = 90 + 30 * lagna - 15
    const e = 90 + 30 * lagna + 15
    const lr = mini ? 168 : 194
    const [ax, ay] = pt(lr, s)
    const [bx, by] = pt(lr, e)
    add("path", { d: `M ${c} ${c} L ${ax} ${ay} A ${lr} ${lr} 0 0 1 ${bx} ${by} Z`, fill: T.tint })

    for (let i = 0; i < 12; i++) {
      const a = 90 + 30 * i
      if (mini) {
        const [gx, gy] = pt(182, a)
        add("text", { x: gx, y: gy + 9, fill: "#D9BC72", "font-size": 26, "text-anchor": "middle", "font-family": "serif" }, SIGNS[i])
        continue
      }
      const [gx, gy] = pt(150, a)
      add("text", { x: gx, y: gy + 4, fill: T.glyph, "font-size": 11, "text-anchor": "middle", "font-family": "serif" }, SIGNS[i])
      // ชื่อเรือนโค้งตามวง: ครึ่งบน (รวมซ้าย-ขวา) เดินตามเข็ม อ่านซ้าย→ขวา / ครึ่งล่างเดินทวนเข็มให้ตัวอักษรไม่กลับหัว
      const hn = ((i - lagna + 12) % 12) + 1
      const top = Math.sin((a * Math.PI) / 180) > -0.01
      const r = top ? 175 : 184 // ขอบล่างตัวอักษรอยู่คนละฝั่ง ชดเชยให้ตัวหนังสืออยู่กลางแถบเท่ากัน
      const [p1x, p1y] = pt(r, top ? a + 15 : a - 15)
      const [p2x, p2y] = pt(r, top ? a - 15 : a + 15)
      const id = `hp${uid}${i}`
      add("path", { id, d: `M ${p1x} ${p1y} A ${r} ${r} 0 0 ${top ? 1 : 0} ${p2x} ${p2y}`, fill: "none" }, null, defs)
      const t = add("text", { fill: hn === 1 ? T.bandTextLagna : T.bandText, "font-size": 12.5, "font-weight": hn === 1 ? "700" : "500" })
      add("textPath", { href: `#${id}`, startOffset: "50%", "text-anchor": "middle" }, (this.wheelValue.bandNames || HOUSE_NAMES)[hn - 1], t)
    }

    // ดาวจรจัดกลุ่มตามราศี — ≥3 ดวงเรือนเดียวตัดแถวละ 2 (แถวนอก r=132 แถวใน r=106) กันล้นช่อง
    const bySign = {}
    for (const p of planets) (bySign[p.rasi] ||= []).push(p.sym)
    for (const si in bySign) {
      const list = bySign[si]
      const base = 90 + 30 * Number(si)
      const wrap = list.length >= 3
      list.forEach((sym, j) => {
        let r = 118, off
        if (wrap) {
          const row = Math.floor(j / 2)
          const inRow = j % 2
          const rowLen = Math.min(2, list.length - row * 2)
          r = row === 0 ? 132 : 106
          off = (inRow - (rowLen - 1) / 2) * 15
        } else {
          off = (j - (list.length - 1) / 2) * 13
        }
        const [px, py] = pt(r, base + off)
        const hot = showHot && sym === hotSym
        const sel = !mini && sym === this.selected
        const g = mini ? svg : add("g", { "data-sym": sym, style: "cursor:pointer" })
        if (!mini) add("circle", { cx: px, cy: py, r: 16, fill: "transparent" }, null, g) // พื้นที่แตะ
        if (sel) add("circle", { cx: px, cy: py, r: 13, fill: "rgba(232,200,122,0.2)", stroke: T.sel, "stroke-width": 1.4 }, null, g)
        if (hot) {
          add("circle", { cx: px, cy: py, r: mini ? 26 : 15, fill: "rgba(255,158,79,0.16)" }, null, g)
          const ring = add("circle", { cx: px, cy: py, r: mini ? 18 : 11.5, fill: "none", stroke: ORANGE,
            "stroke-width": mini ? 3 : 1.6 }, null, g)
          // วงส้มเต้นเป็นจังหวะ ให้ดาวที่เพิ่งย้ายเด่นสุดบนวง (เฉพาะวงใหญ่)
          if (!mini) {
            add("animate", { attributeName: "r", values: "11.5;15.5;11.5", dur: "1.6s", repeatCount: "indefinite" }, null, ring)
            add("animate", { attributeName: "stroke-opacity", values: "1;0.35;1", dur: "1.6s", repeatCount: "indefinite" }, null, ring)
          }
        }
        // เลขไทยตัวหนา+เล็ก หัวเลขตันอ่านไม่ออก — ใช้ตัวใหญ่ขึ้น น้ำหนักเบาลงแทน
        add("text", { x: px, y: py + (mini ? 11 : 7), fill: hot ? T.hot : sel ? T.sel : T.ink,
          "font-size": mini ? 32 : hot ? 21 : 19, "font-weight": hot || sel ? "600" : "400",
          "text-anchor": "middle", "pointer-events": "none" }, this.glyph(sym), g)
      })
    }

    add("circle", { cx: c, cy: c, r: mini ? 44 : 32, fill: `url(#hub${uid})`,
      stroke: `url(#gold${uid})`, "stroke-width": mini ? 6 : 2.5 })
    if (mini) {
      add("text", { x: c, y: c + 12, fill: GOLD, "font-size": 34, "font-weight": "800",
        "text-anchor": "middle" }, this.label("lagna_mark", {}, "ลั"))
    } else {
      add("text", { x: c, y: c - 2, fill: GOLD_DIM, "font-size": 9, "text-anchor": "middle" }, this.label("lagna_word", {}, "ลัคนา"))
      add("text", { x: c, y: c + 14, fill: GOLD, "font-size": 14, "font-weight": "800",
        "text-anchor": "middle" }, lagnaName)
    }
  }
}
